import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Avatar, Button, Empty, Spin, message } from "antd";
import {
  BarChartOutlined,
  RobotOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { StudentAPI, DidacticRoutesAPI } from "../../../../api";
import styles from "./ClassPanel.module.scss";

const studentAPI = new StudentAPI();
const routesAPI = new DidacticRoutesAPI();

/**
 * ClassPanel — panel de clase (Itinerarios, F4).
 * Asistentes IA del itinerario (SELECCIONABLES: el profesor marca cual ve el
 * alumno -> assistantActive_Id) + estudiantes de la clase + acceso al ranking.
 */
export function ClassPanel({
  institutionId,
  classNumber,
  route,
  accessToken,
  activeQuizId,
  onRankingClick,
}) {
  const { t } = useTranslation("evaluations");
  const [students, setStudents] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [activeAssistantId, setActiveAssistantId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    if (!institutionId || !classNumber) {
      setStudents([]);
      return undefined;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await studentAPI.getStudentsByClassnumber(
          accessToken,
          institutionId,
          classNumber,
        );
        if (!ignore) setStudents(res?.students || []);
      } catch (e) {
        if (!ignore) setStudents([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [institutionId, classNumber, accessToken]);

  useEffect(() => {
    let ignore = false;
    if (!route) {
      setAssistants([]);
      setActiveAssistantId(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await routesAPI.getRouteById(accessToken, route);
        const list = Array.isArray(res?.route?.assistants)
          ? res.route.assistants.map((a) => a.assistant).filter(Boolean)
          : [];
        if (!ignore) {
          setAssistants(list);
          setActiveAssistantId(
            res?.route?.assistantActive_Id
              ? String(res.route.assistantActive_Id)
              : null,
          );
        }
      } catch (e) {
        if (!ignore) {
          setAssistants([]);
          setActiveAssistantId(null);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [route, accessToken]);

  // Selecciona (o deselecciona) el asistente activo del itinerario. Es lo que
  // vera el alumno. Opcional: volver a pulsar el activo lo quita.
  const selectAssistant = useCallback(
    async (assistantId) => {
      if (!route || savingId) return;
      const next =
        activeAssistantId === String(assistantId) ? null : String(assistantId);
      setSavingId(assistantId);
      try {
        await routesAPI.updateDidacticRoute(accessToken, route, {
          assistantActive_Id: next || "",
        });
        setActiveAssistantId(next);
        message.success(
          next ? t("assistantSelect.activated") : t("assistantSelect.cleared"),
        );
      } catch (e) {
        message.error(t("assistantSelect.error"));
      } finally {
        setSavingId(null);
      }
    },
    [route, accessToken, activeAssistantId, savingId, t],
  );

  const title = useMemo(
    () =>
      classNumber
        ? `${t("quiz.classLabel")} ${classNumber}`
        : t("studentsPanel.listTitle"),
    [classNumber, t],
  );

  if (!classNumber) {
    return (
      <div className={styles.empty}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("studentsPanel.listDescription")}
        />
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.head}>{title}</div>

      {activeQuizId ? (
        <Button
          block
          icon={<BarChartOutlined />}
          className={styles.rankingBtn}
          onClick={() => onRankingClick?.(activeQuizId)}
        >
          {t("quiz.viewRankingLabel")}
        </Button>
      ) : null}

      <div className={styles.section}>{t("studentsPanel.assignedAI")}</div>
      {assistants.length === 0 ? (
        <div className={styles.muted}>{t("assistantSelect.none")}</div>
      ) : (
        <>
          <div className={styles.hint}>{t("assistantSelect.hint")}</div>
          <ul className={styles.list}>
            {assistants.map((a) => {
              const isActive = activeAssistantId === String(a._id);
              return (
                <li
                  className={`${styles.row} ${styles.selectable} ${
                    isActive ? styles.active : ""
                  }`}
                  key={a._id || a.title}
                  onClick={() => selectAssistant(a._id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") selectAssistant(a._id);
                  }}
                >
                  <Avatar
                    size={32}
                    src={a.miniature}
                    icon={<RobotOutlined />}
                  />
                  <div className={styles.info}>
                    <div className={styles.name}>{a.title}</div>
                    <div className={styles.desc}>
                      {isActive
                        ? t("assistantSelect.activeBadge")
                        : t("studentsPanel.virtualAssistant")}
                    </div>
                  </div>
                  {savingId === a._id ? (
                    <Spin size="small" />
                  ) : isActive ? (
                    <span className={styles.activeBadge}>
                      <CheckOutlined />
                      {t("assistantSelect.activeShort")}
                    </span>
                  ) : (
                    <Button
                      size="small"
                      className={styles.activateBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAssistant(a._id);
                      }}
                    >
                      {t("assistantSelect.activate")}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className={styles.section}>
        {t("studentsPanel.studentsInItinerary")}
      </div>
      {loading ? (
        <div className={styles.center}>
          <Spin size="small" />
        </div>
      ) : students.length === 0 ? (
        <div className={styles.muted}>{t("studentsPanel.noStudents")}</div>
      ) : (
        <ul className={styles.list}>
          {students.map((s) => (
            <li className={styles.row} key={s._id}>
              <Avatar size={32} src={s.avatar}>
                {(s.name || "?").charAt(0)}
              </Avatar>
              <div className={styles.info}>
                <div className={styles.name}>
                  {s.name} {s.surname}
                </div>
                <div className={styles.desc}>
                  {s.classnumber || classNumber}
                </div>
              </div>
              <span className={styles.dot} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ClassPanel;
